<script setup>
import { computed, ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useMapGetter, useStore } from 'dashboard/composables/store.js';
import { usePolicy } from 'dashboard/composables/usePolicy';
import { useAccount } from 'dashboard/composables/useAccount';
import { OnClickOutside } from '@vueuse/components';

import CaptainPaywall from 'dashboard/components-next/captain/pageComponents/Paywall.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import AssistantSwitcher from 'dashboard/components-next/captain/pageComponents/switcher/AssistantSwitcher.vue';
import Policy from 'dashboard/components/policy.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import PaginationFooter from 'dashboard/components-next/pagination/PaginationFooter.vue';
import CreateAssistantDialog from 'dashboard/components-next/captain/pageComponents/assistant/CreateAssistantDialog.vue';

const props = defineProps({
  headerTitle: {
    type: String,
    default: '',
  },
  buttonLabel: {
    type: String,
    default: '',
  },
  showKnowMore: {
    type: Boolean,
    default: true,
  },
  isEmpty: {
    type: Boolean,
    default: false,
  },
  isFetching: {
    type: Boolean,
    default: false,
  },
  buttonPolicy: {
    type: Array,
    default: () => ['administrator'],
  },
  showPaginationFooter: {
    type: Boolean,
    default: true,
  },
  featureFlag: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['close', 'click', 'page-change']);

const { t } = useI18n();
const { isCloudFeatureEnabled } = useAccount();
const { checkPermissions } = usePolicy();
const route = useRoute();
const router = useRouter();
const store = useStore();

const createAssistantDialogRef = ref(null);
const showAssistantSwitcherDropdown = ref(false);

const toggleAssistantSwitcher = () => {
  showAssistantSwitcherDropdown.value = !showAssistantSwitcherDropdown.value;
};

const handleButtonClick = () => {
  emit('click');
};

const handleCreateAssistant = () => {
  createAssistantDialogRef.value.dialogRef.open();
};

const showAssistantSwitcher = computed(() => {
  return [
    'captain_assistants_overview_index',
    'captain_assistants_responses_index',
    'captain_assistants_documents_index',
    'captain_assistants_scenarios_index',
    'captain_assistants_playground_index',
    'captain_assistants_inboxes_index',
    'captain_assistants_guardrails_index',
    'captain_assistants_guidelines_index',
    'captain_assistants_settings_index',
  ].includes(route.name);
});

const assistants = useMapGetter('captainAssistants/getRecords');
const meta = useMapGetter('captainAssistants/getMeta');

const currentAssistantId = computed(() => route.params.assistantId);

const currentAssistant = computed(() => {
  return (
    assistants.value.find(
      assistant => assistant.id === Number(currentAssistantId.value)
    ) || {}
  );
});

const currentAssistantName = computed(() => {
  return currentAssistant.value?.name || '';
});

const isUserAdmin = computed(() => checkPermissions(['administrator']));

const showPaywall = computed(() => {
  if (!props.featureFlag) return false;

  return !isCloudFeatureEnabled(props.featureFlag);
});

const currentPage = computed(() => {
  return meta.value.page || 1;
});

const totalCount = computed(() => {
  return meta.value.totalCount || 0;
});

const itemsPerPage = computed(() => {
  return 25;
});

const handlePageChange = page => {
  emit('page-change', page);
};

const updateActiveAssistantInUiSettings = async assistantId => {
  if (assistantId && isUserAdmin.value) {
    await store.dispatch('updateUISettings', {
      uiSettings: {
        last_active_assistant_id: assistantId,
      },
    });
  }
};

const fetchAssistants = async () => {
  if (assistants.value.length === 0) {
    await store.dispatch('captainAssistants/get');
  }

  const { assistantId } = route.params;

  if (assistants.value.length > 0) {
    if (!assistantId) {
      const { id: firstAssistantId } = assistants.value[0];
      router.push({
        name: route.name,
        params: {
          accountId: route.params.accountId,
          assistantId: firstAssistantId,
        },
      });
    } else {
      updateActiveAssistantInUiSettings(assistantId);
    }
  }
};

onMounted(async () => {
  await fetchAssistants();
});
</script>

<template>
  <section class="flex flex-col w-full h-full overflow-hidden bg-n-surface-1">
    <header class="sticky top-0 z-10 bg-n-surface-1">
      <div class="px-6 py-4 mx-auto max-w-5xl border-b border-n-weak">
        <div class="flex items-center justify-between">
          <div class="flex items-center gap-4">
            <div v-if="showAssistantSwitcher && !showPaywall">
              <div class="flex items-center gap-2">
                <div class="relative flex items-center group">
                  <OnClickOutside
                    @trigger="showAssistantSwitcherDropdown = false"
                  >
                    <Button
                      variant="ghost"
                      color="slate"
                      size="sm"
                      class="flex items-center gap-2 rounded-md group-hover:bg-n-slate-3 hover:bg-n-slate-3 [&>span]:size-4 !px-2.5 !py-1.5"
                      @click="toggleAssistantSwitcher"
                    >
                      <Avatar
                        v-if="currentAssistant && currentAssistant.name"
                        :name="currentAssistant.name"
                        :src="currentAssistant.avatar_url"
                        :size="20"
                        icon-name="i-lucide-bot"
                        rounded-full
                      />
                      <span class="text-sm font-medium text-n-slate-12">
                        {{ currentAssistantName }}
                      </span>
                      <span class="i-lucide-chevron-down size-4 text-n-slate-10" />
                    </Button>

                    <AssistantSwitcher
                      v-if="showAssistantSwitcherDropdown"
                      class="absolute ltr:left-0 rtl:right-0 top-9"
                      @close="showAssistantSwitcherDropdown = false"
                      @create-assistant="handleCreateAssistant"
                    />
                  </OnClickOutside>
                </div>
              </div>
            </div>
            <div class="flex items-center gap-4">
              <div
                v-if="showAssistantSwitcher && !showPaywall && headerTitle"
                class="w-0.5 h-4 rounded-2xl bg-n-weak"
              />
              <span
                v-if="headerTitle"
                class="text-xl font-medium text-n-slate-12"
              >
                {{ headerTitle }}
              </span>
              <div
                v-if="!isEmpty && showKnowMore"
                class="flex items-center gap-2"
              >
                <div class="w-0.5 h-4 rounded-2xl bg-n-weak" />
                <slot name="knowMore" />
              </div>
            </div>
          </div>

          <div class="flex items-center gap-2">
            <slot name="headerActions" />
            <slot name="search" />
            <div
              v-if="!showPaywall && buttonLabel"
              v-on-clickaway="() => emit('close')"
              class="relative group/captain-button"
            >
              <Policy :permissions="buttonPolicy">
                <Button
                  :label="buttonLabel"
                  icon="i-lucide-plus"
                  size="sm"
                  class="group-hover/captain-button:brightness-110"
                  @click="handleButtonClick"
                />
              </Policy>
              <slot name="action" />
            </div>
          </div>
        </div>
        <slot name="subHeader" />
      </div>
    </header>
    <main class="flex-1 px-6 overflow-y-auto">
      <div class="w-full max-w-5xl h-full mx-auto py-4">
        <slot v-if="!showPaywall" name="controls" />
        <div
          v-if="isFetching"
          class="flex items-center justify-center py-10 text-n-slate-11"
        >
          <Spinner />
        </div>
        <div v-else-if="showPaywall">
          <slot name="paywall" />
        </div>
        <div v-else-if="isEmpty">
          <slot name="emptyState" />
        </div>
        <slot v-else name="body" />
        <slot />
      </div>
    </main>
    <footer v-if="showPaginationFooter" class="sticky bottom-0 z-10">
      <PaginationFooter
        :current-page="currentPage"
        :total-items="totalCount"
        :items-per-page="itemsPerPage"
        class="max-w-[67rem]"
        @update:current-page="handlePageChange"
      />
    </footer>
    <CreateAssistantDialog ref="createAssistantDialogRef" type="create" />
  </section>
</template>
